import { createGlobalStyle } from 'styled-components';
import { theme } from './theme';

export const GlobalStyles = createGlobalStyle`
  /* Orchestra palette as CSS variables (used by the Ribbon Symphony welcome scene). */
  :root {
    --kph-gold: #d4af37;       /* strings */
    --kph-copper: #b87333;     /* brass */
    --kph-silver: #c0c0c0;     /* woodwinds */
    --kph-percussion: #bfc0cd; /* percussion (deep enough to read on pure white) */
    --kph-welcome-bg: #ffffff; /* welcome background: pure white per brief */
    --kph-navy-ink: #1a1a2e;   /* primary text on the white field */
    --kph-gold-ink: #8a6d1f;   /* gold dark enough to pass AA as text on white */

    /* Single source of truth for the fixed header's height. The header, the
       main-content offset, and the hero's pull-up all read this, so they can
       never disagree and leave a white strip under the header. */
    --kph-header-offset: 4.5rem; /* 72px */
  }

  @media (max-width: ${theme.breakpoints.md}) {
    :root {
      --kph-header-offset: 4rem; /* 64px */
    }
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    font-size: 16px;
    scroll-behavior: auto;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  body {
    font-family: ${theme.fonts.body};
    font-size: ${theme.fontSizes.base};
    font-weight: ${theme.fontWeights.normal};
    line-height: ${theme.lineHeights.normal};
    color: ${theme.colors.neutral.darkGray};
    background-color: ${theme.colors.neutral.white};
    overflow-x: hidden;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: ${theme.fonts.heading};
    font-weight: ${theme.fontWeights.bold};
    line-height: ${theme.lineHeights.tight};
    color: ${theme.colors.primary.navy};
    margin: 0;
  }

  h1 {
    font-size: ${theme.fontSizes['5xl']};

    @media (max-width: ${theme.breakpoints.md}) {
      font-size: ${theme.fontSizes['4xl']};
    }
  }

  h2 {
    font-size: ${theme.fontSizes['4xl']};

    @media (max-width: ${theme.breakpoints.md}) {
      font-size: ${theme.fontSizes['3xl']};
    }
  }

  h3 {
    font-size: ${theme.fontSizes['3xl']};

    @media (max-width: ${theme.breakpoints.md}) {
      font-size: ${theme.fontSizes['2xl']};
    }
  }

  h4 {
    font-size: ${theme.fontSizes['2xl']};
  }

  h5 {
    font-size: ${theme.fontSizes.xl};
  }

  h6 {
    font-size: ${theme.fontSizes.lg};
  }

  p {
    margin: 0;
    line-height: ${theme.lineHeights.relaxed};
  }

  a {
    color: ${theme.colors.primary.royalBlue};
    text-decoration: none;
    transition: color ${theme.transitions.fast};

    &:hover {
      color: ${theme.colors.primary.gold};
    }
  }

  button {
    font-family: inherit;
    cursor: pointer;
    border: none;
    background: none;
    padding: 0;
  }

  input, textarea, select {
    font-family: inherit;
    font-size: inherit;
  }

  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  ul, ol {
    list-style: none;
  }

  /* Custom scrollbar */
  ::-webkit-scrollbar {
    width: 12px;
  }

  ::-webkit-scrollbar-track {
    background: ${theme.colors.secondary.ivory};
  }

  ::-webkit-scrollbar-thumb {
    background: ${theme.colors.primary.royalBlue};
    border-radius: ${theme.borderRadius.full};

    &:hover {
      background: ${theme.colors.primary.gold};
    }
  }

  /* Selection */
  ::selection {
    background: ${theme.colors.primary.gold};
    color: ${theme.colors.neutral.white};
  }

  /* Focus visible */
  :focus-visible {
    outline: 2px solid ${theme.colors.primary.gold};
    outline-offset: 2px;
  }

  /* Animations */
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slideInUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes slideInDown {
    from {
      transform: translateY(-20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes float {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-20px);
    }
  }

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;
